import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow, parseISO } from 'date-fns';
import { tr } from 'date-fns/locale';
import { api } from '../api/client';
import { useRegisterRefresh } from '../context/PullToRefreshContext';
import { useAuth } from '../context/AuthContext';

function eventIcon(type) {
  const map = {
    task_assigned: '📋',
    task_delegated: '👤',
    deadline_today: '⏰',
    deadline_tomorrow: '📅',
    deadline_overdue: '⚠️',
    task_completed: '✅',
    delete_requested: '🗑️',
    delete_approved: '✅',
    delete_rejected: '❌',
    category_pending: '📂',
    category_approved: '✅',
    category_rejected: '❌',
    category_needs_update: '🔄',
    customer_duplicate: '👥',
    image_deleted: '🖼️',
    leave_started: '🏖️',
    leave_ended: '🏢',
    task_deleted_by_admin: '🗑️',
    delegation_removed: '↩️',
    process_note_added: '📝',
    customer_type_request_pending:  '🏷️',
    customer_type_request_approved: '✅',
    customer_type_request_rejected: '❌',
  };
  return map[type] || '🔔';
}

function DeletedTaskModal({ body, onClose }) {
  let snap = null;
  try { snap = JSON.parse(body); } catch { /* plain text fallback */ }

  return (
    <div className="fixed inset-0 z-[80] bg-black/50 flex items-end sm:items-center" onClick={onClose}>
      <div
        className="bg-white w-full max-w-md mx-auto rounded-t-2xl sm:rounded-2xl p-5 space-y-3 max-h-[85vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-2">
          <h3 className="font-semibold text-gray-900 text-sm">Silinen İş — Anlık Görüntü</h3>
          <span className="text-xs bg-red-100 text-red-700 font-medium px-2 py-0.5 rounded-full">Silindi</span>
        </div>

        {snap ? (
          <dl className="space-y-2 text-sm">
            {[
              ['Kod', snap.code],
              ['Başlık', snap.title],
              ['Kategori', snap.category],
              ['Sorumlu', snap.assignee],
              ['Delege', snap.delegated_to],
              ['Müşteri', snap.customer],
              ['Son Tarih', snap.deadline],
              ['Durum', snap.status],
              ['İş Tanımı', snap.description],
              ['İş Çıktısı', snap.output_text],
              ['Silen Kişi', snap.deleted_by],
            ].filter(([, v]) => v).map(([label, value]) => (
              <div key={label} className="flex gap-2">
                <dt className="text-gray-400 shrink-0 w-24">{label}</dt>
                <dd className="text-gray-800 font-medium break-words">{value}</dd>
              </div>
            ))}
          </dl>
        ) : (
          <p className="text-sm text-gray-600">{body}</p>
        )}

        <p className="text-[10px] text-gray-400 pt-1">Bu pencere bir kez görüntülenebilir. Kapatınca okundu işaretlenir.</p>
        <button type="button" className="btn-primary w-full" onClick={onClose}>
          Anladım, Kapat
        </button>
      </div>
    </div>
  );
}

export default function NotificationPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [unreadCount, setUnreadCount] = useState(0);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [markAllBusy, setMarkAllBusy] = useState(false);
  const [deletedSnapshot, setDeletedSnapshot] = useState(null); // { notifId, body }

  // Onay/red işlem state'leri — key: notification.id
  const [actionBusy, setActionBusy] = useState({});
  const [actionResult, setActionResult] = useState({});
  // Red formu — hangi bildirim için açık: { id, type ('category'|'delete'|'customer_type'), reason, ... }
  const [rejectForm, setRejectForm] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await api.getNotifications();
      setUnreadCount(data.unread_count || 0);
      setItems(data.results || []);
    } catch {
      setError('Bildirimler yüklenemedi.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const broadcastUnread = (count) => {
    window.dispatchEvent(new CustomEvent('unread-count-changed', { detail: { count } }));
  };

  const markReadLocal = async (n) => {
    if (n.is_read) return;
    try {
      await api.markRead(n.id);
      setItems(prev => prev.map(x => x.id === n.id ? { ...x, is_read: true } : x));
      setUnreadCount(c => { const next = Math.max(0, c - 1); broadcastUnread(next); return next; });
    } catch { /* sessizce geç */ }
  };

  const closeDeletedSnapshot = async () => {
    if (deletedSnapshot) {
      const n = items.find(x => x.id === deletedSnapshot.notifId);
      if (n) await markReadLocal(n);
    }
    setDeletedSnapshot(null);
  };

  const openItem = async (n) => {
    if (n.event_type === 'task_deleted_by_admin') {
      if (n.is_read) return; // bir kez görüntülendi, artık açılmaz
      setDeletedSnapshot({ notifId: n.id, body: n.body });
      return;
    }
    await markReadLocal(n);
    if (n.related_task_id) { navigate(`/satis/tasks/${n.related_task_id}`); return; }
    if (n.related_customer_id) { navigate(`/satis/customers/${n.related_customer_id}`); return; }
    navigate('/satis');
  };

  const markAll = async () => {
    setMarkAllBusy(true);
    try {
      await api.markAllRead();
      setUnreadCount(0);
      setItems(prev => prev.map(n => ({ ...n, is_read: true })));
      broadcastUnread(0);
    } catch {
      setError('İşlem başarısız.');
    } finally {
      setMarkAllBusy(false);
    }
  };

  const doImageRestore = async (e, n) => {
    e.stopPropagation();
    setActionBusy(p => ({ ...p, [n.id]: true }));
    try {
      const res = await api.restoreNoteImages(n.related_note_id);
      setActionResult(p => ({ ...p, [n.id]: { ok: true, msg: res.data.detail } }));
      await load();
    } catch (err) {
      setActionResult(p => ({ ...p, [n.id]: { ok: false, msg: err.response?.data?.detail || 'Hata oluştu.' } }));
    } finally {
      setActionBusy(p => ({ ...p, [n.id]: false }));
    }
  };

  const doImageNotDelete = async (e, n) => {
    e.stopPropagation();
    setActionBusy(p => ({ ...p, [n.id]: true }));
    try {
      const res = await api.adminDeleteNote(n.related_note_id);
      setActionResult(p => ({ ...p, [n.id]: { ok: true, msg: res.data.detail } }));
      await load();
    } catch (err) {
      setActionResult(p => ({ ...p, [n.id]: { ok: false, msg: err.response?.data?.detail || 'Hata oluştu.' } }));
    } finally {
      setActionBusy(p => ({ ...p, [n.id]: false }));
    }
  };

  const doApprove = async (e, n, type) => {
    e.stopPropagation();
    setActionBusy(p => ({ ...p, [n.id]: true }));
    setActionResult(p => ({ ...p, [n.id]: null }));
    try {
      let res;
      if (type === 'category') res = await api.approveCategory(n.related_category_id);
      else if (type === 'customer_type') res = await api.reviewCustomerTypeRequest(n.related_type_request_id, 'approve', '');
      else res = await api.approveDelete(n.related_task_id);
      setActionResult(p => ({ ...p, [n.id]: { ok: true, msg: res.data.detail } }));
      await load();
    } catch (err) {
      setActionResult(p => ({ ...p, [n.id]: { ok: false, msg: err.response?.data?.detail || 'Hata oluştu.' } }));
    } finally {
      setActionBusy(p => ({ ...p, [n.id]: false }));
    }
  };

  const openRejectForm = (e, n, type) => {
    e.stopPropagation();
    setRejectForm({ id: n.id, type, categoryId: n.related_category_id, taskId: n.related_task_id, typeRequestId: n.related_type_request_id, reason: '' });
  };

  const doReject = async (e) => {
    e.stopPropagation();
    if (!rejectForm?.reason.trim()) return;
    const { id, type, categoryId, taskId, typeRequestId, reason } = rejectForm;
    setActionBusy(p => ({ ...p, [id]: true }));
    try {
      let res;
      if (type === 'category') res = await api.rejectCategory(categoryId, reason);
      else if (type === 'customer_type') res = await api.reviewCustomerTypeRequest(typeRequestId, 'reject', reason);
      else res = await api.rejectDelete(taskId, reason);
      setActionResult(p => ({ ...p, [id]: { ok: true, msg: res.data.detail } }));
      setRejectForm(null);
      await load();
    } catch (err) {
      setActionResult(p => ({ ...p, [id]: { ok: false, msg: err.response?.data?.detail || 'Hata oluştu.' } }));
    } finally {
      setActionBusy(p => ({ ...p, [id]: false }));
    }
  };

  return (
    <div className="px-4 py-3 pb-24 space-y-4">
      {deletedSnapshot && (
        <DeletedTaskModal body={deletedSnapshot.body} onClose={closeDeletedSnapshot} />
      )}
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs text-gray-500">
          Okunmamış: <span className="font-semibold text-gray-800">{unreadCount}</span>
        </p>
        <button
          type="button"
          className="text-xs font-semibold text-brand-600 disabled:opacity-50"
          onClick={markAll}
          disabled={markAllBusy || unreadCount === 0}
        >
          Tümünü Okundu İşaretle
        </button>
      </div>

      {error && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl p-3">{error}</div>
      )}

      {loading && (
        <div className="flex justify-center py-12">
          <div className="w-7 h-7 border-4 border-brand-500 border-t-transparent rounded-full animate-spin" />
        </div>
      )}

      {!loading && items.length === 0 && (
        <p className="text-sm text-gray-400 text-center py-12">Bildirim yok.</p>
      )}

      <ul className="space-y-2">
        {!loading && items.map((n) => {
          const canApproveCategory    = isAdmin && n.event_type === 'category_pending'              && n.related_category_id;
          const canApproveDelete      = isAdmin && n.event_type === 'delete_requested'              && n.related_task_id;
          const canApproveCustomerType = isAdmin && n.event_type === 'customer_type_request_pending' && n.related_type_request_id;
          const canReviewImage        = isAdmin && n.event_type === 'image_deleted'                 && n.related_note_id;
          const needsApproval = canApproveCategory || canApproveDelete || canApproveCustomerType;
          const approvalType  = canApproveCategory ? 'category' : canApproveCustomerType ? 'customer_type' : 'delete';
          const result  = actionResult[n.id];
          const busy    = actionBusy[n.id];
          const isRejectOpen = rejectForm?.id === n.id;

          return (
            <li key={n.id} className="space-y-1">
              {/* Tıklanabilir kart alanı — aksiyon butonları DIŞARIDA */}
              <div
                role="button"
                tabIndex={0}
                onClick={() => openItem(n)}
                onKeyDown={e => e.key === 'Enter' && openItem(n)}
                className={`card w-full text-left flex gap-3 items-start transition-colors ${
                  n.event_type === 'task_deleted_by_admin' && n.is_read
                    ? 'cursor-default'
                    : 'cursor-pointer'
                } ${
                  !n.is_read
                    ? 'bg-white border-l-4 border-l-brand-500 shadow-sm'
                    : 'bg-gray-50 border-l-4 border-l-transparent opacity-70'
                } ${n.is_mention && !n.is_read ? 'ring-2 ring-brand-200' : ''}`}
              >
                <span className={`text-lg shrink-0 ${n.is_read ? 'opacity-50' : ''}`} aria-hidden>
                  {eventIcon(n.event_type)}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    {!n.is_read && (
                      <span className="w-2 h-2 rounded-full bg-brand-500 shrink-0" aria-label="Okunmamış" />
                    )}
                    {n.is_mention && <span className="text-brand-600 font-bold text-xs">@</span>}
                    <p className={`text-sm ${!n.is_read ? 'font-bold text-gray-900' : 'font-medium text-gray-500'}`}>
                      {n.title}
                    </p>
                  </div>
                  {n.body && (
                    <p className={`text-xs mt-0.5 whitespace-pre-line line-clamp-3 ${n.is_read ? 'text-gray-400' : 'text-gray-500'}`}>
                      {n.body}
                    </p>
                  )}
                  <p className="text-[10px] text-gray-400 mt-1">
                    {formatDistanceToNow(parseISO(n.created_at), { addSuffix: true, locale: tr })}
                  </p>
                  {result && (
                    <p className={`text-xs mt-1 font-medium ${result.ok ? 'text-green-600' : 'text-red-500'}`}>
                      {result.msg}
                    </p>
                  )}
                </div>
              </div>

              {/* Kategori reddedildi → işe git */}
              {n.event_type === 'category_rejected' && n.related_task_id && n.task_category_active === false && (
                <button
                  type="button"
                  className="ml-1 px-3 py-1.5 bg-brand-500 text-white text-xs font-semibold rounded-lg"
                  onClick={() => navigate(`/satis/tasks/${n.related_task_id}`)}
                >
                  İşe Git → Kategori Güncelle
                </button>
              )}

              {/* Kategori silindi — aksiyon tamamlanmadan okundu olmaz */}
              {n.event_type === 'category_needs_update' && n.related_task_id && !n.is_read && (
                <div className="ml-1 flex items-center gap-2 flex-wrap">
                  <span className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5 font-medium">
                    İşlemi tamamlayınca otomatik kapanır
                  </span>
                  <button
                    type="button"
                    className="px-3 py-1.5 bg-brand-500 text-white text-xs font-semibold rounded-lg"
                    onClick={() => navigate(`/satis/tasks/${n.related_task_id}`)}
                  >
                    İşe Git → Kategori Seç
                  </button>
                </div>
              )}

              {/* Görsel silindi — Geri Al / Kaldır */}
              {canReviewImage && !result?.ok && (
                <div className="pl-1 flex gap-2 flex-wrap">
                  <button
                    type="button"
                    className="px-3 py-1.5 bg-green-500 text-white text-xs font-semibold rounded-lg disabled:opacity-50"
                    disabled={busy}
                    onClick={(e) => doImageRestore(e, n)}
                  >
                    {busy ? 'İşleniyor…' : '↩ Geri Al'}
                  </button>
                  <button
                    type="button"
                    className="px-3 py-1.5 bg-red-500 text-white text-xs font-semibold rounded-lg disabled:opacity-50"
                    disabled={busy}
                    onClick={(e) => doImageNotDelete(e, n)}
                  >
                    {busy ? 'İşleniyor…' : '✕ Kaldır'}
                  </button>
                </div>
              )}

              {/* Onay / Red butonları — kart DIŞINDA, tıklama çakışmaz */}
              {needsApproval && !n.is_read && !result?.ok && (
                <div className="pl-1 space-y-2">
                  {!isRejectOpen && (
                    <div className="flex gap-2 flex-wrap">
                      <button
                        type="button"
                        className="px-3 py-1.5 bg-green-500 text-white text-xs font-semibold rounded-lg disabled:opacity-50"
                        disabled={busy}
                        onClick={(e) => doApprove(e, n, approvalType)}
                      >
                        {busy ? 'İşleniyor…' : '✓ Onayla'}
                      </button>
                      <button
                        type="button"
                        className="px-3 py-1.5 bg-red-500 text-white text-xs font-semibold rounded-lg disabled:opacity-50"
                        disabled={busy}
                        onClick={(e) => openRejectForm(e, n, approvalType)}
                      >
                        ✕ Reddet
                      </button>
                    </div>
                  )}

                  {/* Red nedeni formu — button dışında olduğu için yazma problemi yok */}
                  {isRejectOpen && (
                    <div className="card space-y-2">
                      <p className="text-xs font-medium text-gray-700">Red nedeni:</p>
                      <textarea
                        className="input min-h-[72px] resize-none"
                        placeholder="Red nedenini yazın…"
                        value={rejectForm.reason}
                        onChange={e => setRejectForm(f => ({ ...f, reason: e.target.value }))}
                        autoFocus
                      />
                      <div className="flex gap-2">
                        <button
                          type="button"
                          className="px-3 py-1.5 bg-red-500 text-white text-xs font-semibold rounded-lg disabled:opacity-50"
                          disabled={busy || !rejectForm.reason.trim()}
                          onClick={doReject}
                        >
                          {busy ? 'Gönderiliyor…' : 'Reddi Gönder'}
                        </button>
                        <button
                          type="button"
                          className="px-3 py-1.5 bg-gray-100 text-gray-700 text-xs font-semibold rounded-lg"
                          onClick={() => setRejectForm(null)}
                        >
                          İptal
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
